import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ClickableAvatar } from "../components/ClickableAvatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  Cake,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  RefreshCcw,
  MessageSquare,
  Phone,
  User,
} from "lucide-react";
import { WhatsApp } from "../components/ui/whatsapp";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  addMonths,
  subMonths,
  isToday,
} from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { key: "sunday", label: "Sun" },
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
];

const parseMonthParam = (param) => {
  if (!param) return null;
  const parts = param.split("-");
  if (parts.length === 2) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    if (!isNaN(year) && !isNaN(month) && month >= 1 && month <= 12) {
      return new Date(year, month - 1, 1);
    }
  }
  return null;
};

const parseDateParam = (param) => {
  if (!param) return null;
  const parts = param.split("-");
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    if (
      !isNaN(year) &&
      !isNaN(month) &&
      !isNaN(day) &&
      month >= 1 &&
      month <= 12 &&
      day >= 1 &&
      day <= 31
    ) {
      return new Date(year, month - 1, day);
    }
  }
  return null;
};

const getOrdinalSuffix = (num) => {
  if (num <= 0) return "";
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) {
    return num + "st";
  }
  if (j === 2 && k !== 12) {
    return num + "nd";
  }
  if (j === 3 && k !== 13) {
    return num + "rd";
  }
  return num + "th";
};

export default function CalendarPage() {
  const { currentBranch } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Month-level state: lightweight per-day counts for calendar grid dots
  const [dayCounts, setDayCounts] = useState({});
  // Day-level state: full event details fetched on day click
  const [selectedDayEvents, setSelectedDayEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDay, setLoadingDay] = useState(false);
  const [filter, setFilter] = useState("all");

  const [currentDate, setCurrentDate] = useState(() => {
    const mParam = new URLSearchParams(window.location.search).get("month");
    const parsedM = parseMonthParam(mParam);
    if (parsedM) return parsedM;
    const dParam = new URLSearchParams(window.location.search).get("date");
    const parsedD = parseDateParam(dParam);
    if (parsedD) return new Date(parsedD.getFullYear(), parsedD.getMonth(), 1);
    return new Date();
  });

  const selectedDate = useMemo(() => {
    const dParam = searchParams.get("date");
    const parsedD = parseDateParam(dParam);
    if (parsedD) return parsedD;
    return new Date(); // default to today
  }, [searchParams]);

  useEffect(() => {
    const mParam = searchParams.get("month");
    const parsedM = parseMonthParam(mParam);
    const dParam = searchParams.get("date");
    const parsedD = parseDateParam(dParam);

    let targetMonth;
    if (parsedM) {
      targetMonth = parsedM;
    } else if (parsedD) {
      targetMonth = new Date(parsedD.getFullYear(), parsedD.getMonth(), 1);
    } else {
      targetMonth = new Date();
    }

    if (
      targetMonth.getFullYear() !== currentDate.getFullYear() ||
      targetMonth.getMonth() !== currentDate.getMonth()
    ) {
      setCurrentDate(
        new Date(targetMonth.getFullYear(), targetMonth.getMonth(), 1),
      );
    }
  }, [searchParams, currentDate]);

  // Fetch lightweight month counts when month or branch changes
  useEffect(() => {
    fetchMonthCounts();
  }, [currentDate, currentBranch]);

  // Fetch full day events when a date is selected
  useEffect(() => {
    if (selectedDate) {
      fetchDayEvents(format(selectedDate, "yyyy-MM-dd"));
    } else {
      setSelectedDayEvents(null);
    }
  }, [selectedDate, currentBranch]);

  const fetchMonthCounts = async () => {
    setLoading(true);
    setSelectedDayEvents(null);
    try {
      const startDate = format(startOfMonth(currentDate), "yyyy-MM-dd");
      const endDate = format(endOfMonth(currentDate), "yyyy-MM-dd");
      const params = new URLSearchParams({ startDate, endDate });
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      const res = await api.get(`/calendar/month-counts?${params.toString()}`);
      setDayCounts(res.data || {});
    } catch (err) {
      console.error(err);
      toast.error("Failed to load calendar data");
    } finally {
      setLoading(false);
    }
  };

  const fetchDayEvents = async (dateStr) => {
    setLoadingDay(true);
    try {
      const params = new URLSearchParams({ date: dateStr });
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      const res = await api.get(`/calendar/day-events?${params.toString()}`);
      setSelectedDayEvents(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load day events");
    } finally {
      setLoadingDay(false);
    }
  };

  const getCountsForDay = (dateStr) => {
    return dayCounts[dateStr] || { b: 0, r: 0, p: 0 };
  };

  // Month-level totals derived from per-category dayCounts
  const monthStats = useMemo(() => {
    let b = 0,
      r = 0,
      p = 0;
    Object.values(dayCounts).forEach((d) => {
      b += d.b || 0;
      r += d.r || 0;
      p += d.p || 0;
    });
    return { b, r, p, total: b + r + p };
  }, [dayCounts]);

  const monthTotal = monthStats.total;

  const eventTypeColor = (type) => {
    switch (type) {
      case "birthday":
        return "bg-pink-500/15 text-pink-500 border-pink-500/20";
      case "renewal":
        return "bg-amber-500/15 text-amber-500 border-amber-500/20";
      case "partial":
        return "bg-blue-500/15 text-blue-500 border-blue-500/20";
      default:
        return "bg-zinc-500/15 text-zinc-400 border-zinc-500/20";
    }
  };

  const eventTypeIcon = (type) => {
    switch (type) {
      case "birthday":
        return <Cake className="h-3.5 w-3.5" />;
      case "renewal":
        return <RefreshCcw className="h-3.5 w-3.5" />;
      case "partial":
        return <CreditCard className="h-3.5 w-3.5" />;
      default:
        return null;
    }
  };

  const handleMonthChange = (newMonth) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("month", format(newMonth, "yyyy-MM"));
    nextParams.delete("date"); // clear selected day on month navigation
    setSearchParams(nextParams);
  };

  const handleDayClick = (day) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("date", format(day, "yyyy-MM-dd"));
    nextParams.set("month", format(day, "yyyy-MM"));
    setSearchParams(nextParams);
  };

  return (
    <div className="space-y-2 md:space-y-3" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black font-heading tracking-tight uppercase">
            Calendar
          </h1>
          <p className="text-sm text-muted-foreground">
            View birthdays, renewals, and payment reminders
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className=" !hidden flex overflow-x-auto gap-1.5 pb-1 -mx-4 px-4 md:grid md:grid-cols-4 md:gap-3 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {[{ label: "Total", value: monthTotal, color: "" }].map((s) => (
          <Card key={s.label} className="bg-card border-border">
            <CardContent className="p-2 text-center">
              <p className={`text-xl font-black font-heading ${s.color}`}>
                {s.value}
              </p>
              <p className="text-[10px] font-mono tracking-tight md:tracking-widest text-muted-foreground">
                {s.label}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Grid Layout matching Followups.js */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 items-start">
        {/* Left: Calendar Card */}
        <Card className="lg:col-span-5 bg-card border-border">
          <CardHeader className="flex flex-row items-center justify-between pb-2 px-4 py-2">
            <div className="flex flex-col gap-1">
              <CardTitle className="text-sm font-heading uppercase tracking-wide leading-none">
                Calendar
              </CardTitle>
              <div className="flex items-center gap-2 text-[10px] font-mono font-bold leading-none mt-1">
                {monthStats.total > 0 && (
                  <span className="text-muted-foreground">
                    Total: {monthStats.total}
                  </span>
                )}
                {monthStats.b > 0 && (
                  <span className="text-pink-500">BD: {monthStats.b}</span>
                )}
                {monthStats.r > 0 && (
                  <span className="text-amber-500">RN: {monthStats.r}</span>
                )}
                {monthStats.p > 0 && (
                  <span className="text-blue-500">PP: {monthStats.p}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleMonthChange(subMonths(currentDate, 1))}
                data-testid="prev-month-btn"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium min-w-[100px] text-center">
                {format(currentDate, "MMMM yyyy")}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleMonthChange(addMonths(currentDate, 1))}
                data-testid="next-month-btn"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-1">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {DAYS_OF_WEEK.map((d) => (
                    <div
                      key={d.key}
                      className="text-center text-[10px] font-mono uppercase tracking-widest text-muted-foreground py-2"
                    >
                      {d.label}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-0.5">
                  {eachDayOfInterval({
                    start: startOfMonth(currentDate),
                    end: endOfMonth(currentDate),
                  }).map((day, idx) => {
                    const dayOfWeek = day.getDay();
                    const isFirstDay = idx === 0;
                    const dateStr = format(day, "yyyy-MM-dd");
                    const c = dayCounts[dateStr] || { b: 0, r: 0, p: 0 };
                    const count = c.b + c.r + c.p;
                    const hasEvents = count > 0;
                    const today = isToday(day);
                    const isSelected =
                      selectedDate && isSameDay(day, selectedDate);

                    return (
                      <div
                        key={day.toISOString()}
                        style={{
                          gridColumn: isFirstDay ? dayOfWeek + 1 : undefined,
                        }}
                        onClick={() => handleDayClick(day)}
                        className={`min-h-[60px] sm:min-h-[80px] p-1 sm:p-2 rounded-sm border cursor-pointer transition-all ${
                          today
                            ? "border-primary bg-primary/5 shadow-[0_0_10px_rgba(var(--primary),0.1)]"
                            : isSelected
                              ? "border-primary bg-primary/5"
                              : hasEvents
                                ? "border-border bg-secondary/30 hover:bg-secondary/50"
                                : "border-border/50 bg-background hover:bg-secondary/20"
                        }`}
                        data-testid={`calendar-day-${dateStr}`}
                      >
                        <div
                          className={`text-[10px] font-medium mb-1 flex justify-between ${today ? "text-primary" : ""}`}
                        >
                          <span>{format(day, "d")}</span>
                          {hasEvents && (
                            <span
                              className={`text-[9px] font-bold w-4 h-4 flex items-center justify-center rounded-full ${c.b > 0 ? "bg-pink-500 text-white" : c.r > 0 ? "bg-amber-500 text-white" : "bg-blue-500 text-white"}`}
                            >
                              {count}
                            </span>
                          )}
                        </div>
                        {hasEvents && (
                          <div className="space-y-1">
                            <div className="flex gap-1 flex-wrap">
                              {c.b > 0 && (
                                <div
                                  className="h-1.5 w-1.5 rounded-full bg-pink-500"
                                  title={`${c.b} Birthday`}
                                />
                              )}
                              {c.r > 0 && (
                                <div
                                  className="h-1.5 w-1.5 rounded-full bg-amber-500"
                                  title={`${c.r} Renewal`}
                                />
                              )}
                              {c.p > 0 && (
                                <div
                                  className="h-1.5 w-1.5 rounded-full bg-blue-500"
                                  title={`${c.p} Partial`}
                                />
                              )}
                            </div>
                            <div className="flex flex-wrap gap-x-1.5 gap-y-0 text-[9px] font-mono font-bold leading-none mt-1">
                              {c.b > 0 && (
                                <span className="text-pink-500">BD:{c.b}</span>
                              )}
                              {c.r > 0 && (
                                <span className="text-amber-500">RN:{c.r}</span>
                              )}
                              {c.p > 0 && (
                                <span className="text-blue-500">PP:{c.p}</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-center gap-x-4 gap-y-2 sm:gap-x-6 mt-4 pt-4 border-t border-border flex-wrap">
                  {[
                    { color: "bg-pink-500", label: "Birthday" },
                    { color: "bg-amber-500", label: "Renewal" },
                    { color: "bg-blue-500", label: "Partial" },
                    {
                      color: "text-pink-500",
                      label: "BD",
                      desc: "Birthday",
                      type: "text",
                    },
                    {
                      color: "text-amber-500",
                      label: "RN",
                      desc: "Renewal Due",
                      type: "text",
                    },
                    {
                      color: "text-blue-500",
                      label: "PP",
                      desc: "Partial Payment",
                      type: "text",
                    },
                  ].map((l) => (
                    <div key={l.label} className="flex items-center gap-1.5">
                      {l.type === "text" ? (
                        <span
                          className={`text-[9px] font-mono font-bold ${l.color}`}
                        >
                          {l.label}
                        </span>
                      ) : (
                        <div
                          className={`w-2.5 h-2.5 rounded-full ${l.color}`}
                        />
                      )}
                      <span className="text-[11px] text-muted-foreground">
                        {l.desc || l.label}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Right: Day Details Card */}
        <Card className="lg:col-span-7 bg-card border-border flex flex-col h-full min-h-[500px] lg:h-[calc(100vh-280px)]">
          <CardHeader className="px-3 py-2 border-b border-border shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-primary" />
                <CardTitle className="text-sm font-heading uppercase tracking-wide">
                  {selectedDate
                    ? format(selectedDate, "EEEE, MMM d, yyyy")
                    : "Daily Details"}
                </CardTitle>
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger
                  className="w-[120px] h-8 text-xs"
                  data-testid="filter-select"
                >
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  <SelectItem value="birthday">Birthdays</SelectItem>
                  <SelectItem value="renewal">Renewals</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-0 scroll-smooth">
            {!selectedDate ? (
              <div className="flex flex-col items-center justify-center h-full p-8 text-center text-muted-foreground opacity-60">
                <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mb-3">
                  <CalendarIcon className="h-6 w-6" />
                </div>
                <p className="text-sm font-medium">
                  Select a day from the calendar
                </p>
                <p className="text-xs mt-1">to view scheduled events</p>
              </div>
            ) : loadingDay ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
              </div>
            ) : (
              (() => {
                const allEvents = [
                  ...(selectedDayEvents?.birthdays || []).map((e) => ({
                    type: "birthday",
                    ...e,
                  })),
                  ...(selectedDayEvents?.renewals || []).map((e) => ({
                    type: "renewal",
                    ...e,
                  })),
                  ...(selectedDayEvents?.partialPayments || []).map((e) => ({
                    type: "partial",
                    ...e,
                  })),
                ];
                const filtered =
                  filter === "all"
                    ? allEvents
                    : allEvents.filter((e) => e.type === filter);

                if (filtered.length === 0)
                  return (
                    <div className="flex flex-col items-center justify-center h-full p-8 text-center text-muted-foreground opacity-60">
                      <MessageSquare className="h-10 w-10 mb-3" />
                      <p className="text-sm">
                        No events scheduled for this day
                      </p>
                    </div>
                  );

                return (
                  <div className="p-1 md:p-4 space-y-2">
                    {/* Events List */}
                    <div className="space-y-2">
                      {filtered.map((event, i) => (
                        <div
                          key={`${event.type}-${i}`}
                          className="group bg-card border border-border rounded-xl hover:border-primary/50 transition-colors shadow-sm overflow-hidden"
                          data-testid={`event-${event.type}-${i}`}
                        >
                          <div className="px-2 md:px-3 py-2 md:py-3 flex items-center justify-between gap-2 select-none">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <ClickableAvatar
                                src={event.photo}
                                name={event.memberName || event.name}
                                className="h-8 md:h-10 w-8 md:w-10 shrink-0 ring-1 ring-border group-hover:ring-primary/30 transition-all"
                                fallbackClassName="bg-primary/10 text-primary text-xs font-semibold"
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-baseline gap-1.5 flex-wrap">
                                  <p className="font-semibold text-sm truncate">
                                    {event.memberName || event.name}
                                  </p>
                                  {event.phone && (
                                    <span className="text-[10px] text-muted-foreground flex items-center gap-0.5 font-mono">
                                      {event.phone}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                  {event.type === "birthday"
                                    ? (() => {
                                        const birthYear = event.dob ? parseInt(event.dob.split("-")[0], 10) : null;
                                        const eventYear = selectedDate ? selectedDate.getFullYear() : new Date().getFullYear();
                                        const age = (birthYear && !isNaN(birthYear)) ? eventYear - birthYear : null;
                                        return `Dob: ${event.dob || "—"}${age ? ` (${getOrdinalSuffix(age)} Birthday)` : ""}`;
                                      })()
                                    : event.type === "renewal"
                                      ? `End Date: ${event.endDate?.slice(0, 10) || "—"}`
                                      : `Next Payment: ${event.nextPaymentDate?.slice(0, 10) || "—"}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 md:gap-2 shrink-0">
                              <Badge
                                className={`${eventTypeColor(event.type)} border text-[10px] font-mono uppercase flex items-center gap-1`}
                              >
                                {eventTypeIcon(event.type)}
                                {event.type === "birthday"
                                  ? "Birthday"
                                  : event.type === "renewal"
                                    ? "Renewal"
                                    : "Payment Due"}
                              </Badge>
                            </div>
                          </div>
                          <div
                            className="flex gap-1 md:gap-2 mb-2 ml-3 justify-center flex-wrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 text-[9px] gap-1 px-2 font-heading uppercase tracking-wide bg-background hover:bg-secondary"
                              onClick={() =>
                                navigate(
                                  `/members/${event.memberId || event.id}`,
                                )
                              }
                            >
                              <User className="h-2.5 w-2.5" /> Profile
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 text-[9px] gap-1 px-2 font-heading uppercase tracking-wide bg-background hover:bg-secondary text-primary border-primary/20 hover:bg-primary/5"
                              onClick={() =>
                                navigate(
                                  `/members/${event.memberId || event.id}?tab=followups`,
                                )
                              }
                            >
                              <MessageSquare className="h-2.5 w-2.5" />{" "}
                              Follow-up
                            </Button>
                            {event.phone && (
                              <>
                                <a
                                  href={`tel:${event.phone}`}
                                  className="text-[9px] h-6 text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-950/30 px-2 rounded-md hover:bg-sky-100 dark:hover:bg-sky-950/60 flex items-center gap-1 font-semibold transition-colors border border-sky-100 dark:border-sky-900/50 hover:border-sky-300 dark:hover:border-sky-800"
                                >
                                  <Phone className="h-2.5 w-2.5" /> Call
                                </a>
                                <a
                                  href={`https://wa.me/${event.phone}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[9px] h-6 text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 px-2 rounded-md hover:bg-emerald-100 dark:hover:bg-emerald-950/60 flex items-center gap-1 font-semibold transition-colors border border-emerald-100 dark:border-emerald-900/50 hover:border-emerald-300 dark:hover:border-emerald-800"
                                >
                                  <WhatsApp className="h-2.5 w-2.5" /> WhatsApp
                                </a>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
